import cx from 'classnames';
import type { PropsWithChildren, ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Header } from '../header';
import { SettingsModal } from '../settings-modal';
import { Sidebar } from '../sidebar';
import type { Layout as LayoutStore } from './context';
import { HEADER_HEIGHT, LayoutContext, RAIL_COLLAPSED, RAIL_EXPANDED } from './context';
import { Device, Theme } from '@livecontrol/core-ui';

const PIN_STORAGE_KEY = 'cc.sidebar.pinned';

const readPinned = (): boolean => {
  try {
    return localStorage.getItem(PIN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const Main = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  transition: padding-left var(--transition);
`;

const MainContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 0px;
  justify-content: center;

  // .x-${Device.MOBILE} & {
  //   padding: 16px 0px;
  // }
`;

/*
 * Mirrors the shared Scaffold's content wrapper so existing routes keep the same
 * Bootstrap context (container-fluid row). Command Center is always dark, so the
 * content block is `display: block` rather than the light-theme flex layout.
 */
const Content = styled.div`
  display: block;
  padding: ${HEADER_HEIGHT + 26}px 30px 56px;
`;

export const Layout = ({
  children,
  sidebar = true
}: PropsWithChildren<{ sidebar?: boolean }>): ReactElement => {
  const viewport = Theme.useViewport();
  const isMobile = Device.isMobile(viewport);

  const [pinned, setPinnedState] = useState(readPinned);
  const [hovered, setHovered] = useState(false);

  const setPinned = useCallback((next: boolean): void => {
    setPinnedState(next);

    try {
      localStorage.setItem(PIN_STORAGE_KEY, String(next));
    } catch {
      // Ignore storage failures (private mode, etc.).
    }
  }, []);

  const store: LayoutStore = useMemo(
    () => ({
      viewport,
      sidebar,
      pinned,
      hovered,
      expanded: pinned || hovered,
      setPinned,
      setHovered
    }),
    [viewport, sidebar, pinned, hovered, setPinned]
  );

  // Content shifts with the pinned rail only; hovering overlays the content.
  const paddingLeft = isMobile || !sidebar ? 0 : pinned ? RAIL_EXPANDED : RAIL_COLLAPSED;

  return (
    <LayoutContext.Provider value={store}>
      <div className={cx('d-flex', 'flex-column', 'min-vh-100', `x-${Device.from(viewport)}`)}>
        {sidebar && <Sidebar />}
        <SettingsModal />

        <Main style={{ paddingLeft }}>
          <Header />
          <MainContainer>
            <Content className='container-fluid row'>{children}</Content>
          </MainContainer>
        </Main>
      </div>
    </LayoutContext.Provider>
  );
};
